import { Box } from '@material-ui/core';
import { formatMoney, formatPercent, Money, Product, Program } from '@nemuru-eng/js-api-client';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { Text } from '@/components/shared/components/Text';

export interface LoanConditionsDisplayProps {
  titleTextId: string;
  product: Product;
  program: Program;
  principal: Money;
  period: number | null;
  annualInterest: number | null;
  apr: number;
  quota: Money | null;
  formGridContainerProps?: FormGridOptions;
  singleColumn?: boolean;
  noLabel?: boolean;
}

export const LoanConditionsDisplay = ({
  titleTextId,
  product,
  program,
  principal,
  period,
  annualInterest,
  apr,
  quota,
  singleColumn = false,
  noLabel = false,
}: LoanConditionsDisplayProps) => {
  const { getTextIdTranslation } = useTranslation();

  const NoLabelFields = () => {
    const Field = ({ children }) => (
      <Font
        variant="h5"
        color="textPrimary"
        style={{ height: '28px', textAlign: 'right', alignContent: 'center', paddingLeft: '8px' }}
      >
        {children}
      </Font>
    );

    return (
      <>
        <Field>{getTextIdTranslation(product)}</Field>
        <Field>{getTextIdTranslation(program)}</Field>
        <Field>{formatMoney(principal)}</Field>
        <Field>{`${period} ${getTextIdTranslation('MONTHS')}`}</Field>
        {quota && <Field>{formatMoney(quota)}</Field>}
        <Field>{annualInterest ? formatPercent(annualInterest) : '-'}</Field>
        <Field>{formatPercent(apr)}</Field>
      </>
    );
  };

  const Fields = () => (
    <>
      <ReadOnlyDataRow labelTextId={'PRODUCT'} value={getTextIdTranslation(product)} />
      <ReadOnlyDataRow labelTextId={'PROGRAM'} value={getTextIdTranslation(program)} />
      <ReadOnlyDataRow labelTextId={'PRINCIPAL'} value={formatMoney(principal)} />
      <ReadOnlyDataRow labelTextId={'TERM'} value={`${period} ${getTextIdTranslation('MONTHS')}`} />
      {quota && <ReadOnlyDataRow labelTextId={'MONTHLY_QUOTA'} value={formatMoney(quota)} />}
      <ReadOnlyDataRow labelTextId={'TIN'} value={annualInterest ? formatPercent(annualInterest) : null} />
      <ReadOnlyDataRow labelTextId={'TAE'} value={formatPercent(apr)} />
    </>
  );

  const RenderFields = noLabel ? <NoLabelFields /> : <Fields />;

  return (
    <Box display={'flex'} width={'100%'} flexDirection={'column'} gridGap={8}>
      <Font variant="subtitle2" color="title" style={{ textAlign: 'end' }}>
        <Text strong tId={titleTextId} />
      </Font>
      {singleColumn ? (
        <Box display={'flex'} flexDirection={'column'} height={'100%'}>
          {RenderFields}
        </Box>
      ) : (
        <>
          <FormGridContainer>{RenderFields}</FormGridContainer>
          <CustomDivider />
        </>
      )}
    </Box>
  );
};
